import { type ReactNode } from 'react'
import { TButton } from 'app/components/buttons'
import { useReducedMotion } from '@mantine/hooks'
import { Box, Group, Text, Transition } from '@mantine/core'
import { useParentHasRoom } from '@hooks'
import { useTranslation } from '@front/model'
import { TDrawer } from './TDrawer'

const OPEN_EASING = 'cubic-bezier(0.34, 1.3, 0.64, 1)' as const
const CLOSE_EASING = 'ease' as const
const DEFAULT_MIN_SIBLING_WIDTH = 400

export type TPushPanelProps = {
  opened: boolean
  onClose: () => void
  title?: ReactNode
  isChildDrawer?: boolean
  headerActions?: ReactNode
  hideCloseButton?: boolean
  width: number
  minSiblingWidth?: number
  children?: ReactNode
}

export const TPushPanel = ({
  width,
  minSiblingWidth = DEFAULT_MIN_SIBLING_WIDTH,
  ...panelProps
}: TPushPanelProps): ReactNode => {
  const { ref, hasRoom } = useParentHasRoom(width + minSiblingWidth)

  return (
    <Box ref={ref} display='contents'>
      {hasRoom ? <PushedPanel {...panelProps} width={width} /> : <TDrawer {...panelProps} size='100%' />}
    </Box>
  )
}

const PushedPanel = ({
  opened,
  onClose,
  title,
  isChildDrawer,
  headerActions,
  hideCloseButton = false,
  width,
  children,
}: Omit<TPushPanelProps, 'minSiblingWidth'>): ReactNode => {
  const { t } = useTranslation()
  const reduceMotion = useReducedMotion()
  const canBounce = opened && !reduceMotion
  const timingFunction = canBounce ? OPEN_EASING : CLOSE_EASING

  return (
    <Transition
      mounted={opened}
      duration={200}
      exitDuration={200}
      timingFunction={timingFunction}
      transition={{
        transitionProperty: 'width, opacity',
        common: { overflow: 'hidden' },
        in: { width, opacity: 1 },
        out: { width: 0, opacity: 0 },
      }}
    >
      {(transitionStyles) => (
        <Box
          component='aside'
          className='flex h-full shrink-0 flex-col border-l border-[var(--mantine-color-default-border)] bg-[var(--mantine-color-body)]'
          style={transitionStyles}
        >
          <Box w={width} className='flex h-full shrink-0 flex-col'>
            <Group
              align='center'
              justify='space-between'
              wrap='nowrap'
              px='xl'
              py='lg'
              className='border-b border-[var(--mantine-color-default-border)]'
            >
              <Group gap='sm' miw={0} wrap='nowrap'>
                {!hideCloseButton && (
                  <TButton
                    buttonType='icon-full'
                    icon={isChildDrawer ? 'chevron_left' : 'close'}
                    variant='gray'
                    onClick={onClose}
                    aria-label={isChildDrawer ? t('Go back') : t('Close')}
                  />
                )}
                {typeof title === 'string' ? (
                  <Text fw='bold' size='lg'>
                    {title}
                  </Text>
                ) : (
                  title
                )}
              </Group>
              {headerActions && (
                <Group gap='sm' wrap='nowrap'>
                  {headerActions}
                </Group>
              )}
            </Group>
            <Box className='flex flex-1 flex-col overflow-auto' p='xl'>
              {children}
            </Box>
          </Box>
        </Box>
      )}
    </Transition>
  )
}
